// /admin/templates (architecture sprint-11 §9.5). Built-ins first. Standard cannot be deleted; neither
// built-in can be renamed. Copying is the only way to make a new template (no "save project as template").
import { Copy, LayoutTemplate, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useCopyTemplate, useDeleteTemplate, useRenameTemplate, useTemplates } from '@/hooks/use-templates';
import type { TemplateListItem } from '@/types/settings';

const MAX_NAME_LENGTH = 60;

function settingsCount(n: number): string {
  return n === 1 ? '1 setting' : `${n} settings`;
}

type NameDialogState = { kind: 'copy' | 'rename'; template: TemplateListItem } | null;

export function TemplateNameDialog({ state, onClose }: { state: NameDialogState; onClose: () => void }): JSX.Element {
  const copy = useCopyTemplate();
  const rename = useRenameTemplate();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lastState, setLastState] = useState<NameDialogState>(null);
  if (state !== lastState) {
    setLastState(state);
    setName(state ? (state.kind === 'copy' ? `${state.template.name} (copy)`.slice(0, MAX_NAME_LENGTH) : state.template.name) : '');
    setError(null);
  }
  const pending = copy.isPending || rename.isPending;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!state) return;
    const trimmed = name.trim();
    if (!trimmed) return setError('Give the template a name');
    const done = { onSuccess: (): void => (toast.success(state.kind === 'copy' ? 'Template copied.' : 'Template renamed.'), onClose()) };
    if (state.kind === 'copy') copy.mutate({ sourceTemplateId: state.template.id, name: trimmed }, done);
    else rename.mutate({ id: state.template.id, name: trimmed }, done);
  }

  return (
    <Dialog open={state !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-3" noValidate>
          <DialogHeader>
            <DialogTitle>{state?.kind === 'rename' ? 'Rename template' : 'Copy template'}</DialogTitle>
            <DialogDescription>
              {state?.kind === 'rename' ? 'Template names are unique.' : `A new template with every setting of ${state?.template.name ?? ''}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="template-name">Name</Label>
            <Input id="template-name" value={name} maxLength={MAX_NAME_LENGTH} onChange={(e) => setName(e.target.value)} />
            {error && <p className="text-xs text-danger-fg">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="submit" className="flex-1" disabled={pending}>
              {pending ? 'Saving…' : state?.kind === 'rename' ? 'Rename' : 'Copy'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TemplatesPage(): JSX.Element {
  const templates = useTemplates();
  const remove = useDeleteTemplate();
  const [nameDialog, setNameDialog] = useState<NameDialogState>(null);
  const [deleting, setDeleting] = useState<TemplateListItem | null>(null);

  return (
    <Page width="4xl">
      <PageHeader
        title="Templates"
        hint="A new project copies its template's settings once. Later template changes don't affect existing projects."
      />
      {!templates.data ? (
        <Skeleton className="h-40 w-full" />
      ) : templates.data.length === 0 ? (
        <EmptyState title="No templates" icon={LayoutTemplate} />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {templates.data.map((t) => (
            <li key={t.id} className="card flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/admin/templates/${t.id}`} className="truncate text-sm text-link hover:underline">
                    {t.name}
                  </Link>
                  {t.builtinKey !== null && <Badge variant="neutral">Built-in</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{settingsCount(t.valueCount)}</p>
              </div>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={`Actions for ${t.name}`}>
                    <MoreHorizontal aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to={`/admin/templates/${t.id}`}>Open</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setNameDialog({ kind: 'copy', template: t })}>
                    <Copy aria-hidden="true" />
                    Copy
                  </DropdownMenuItem>
                  {t.renamable && (
                    <DropdownMenuItem onSelect={() => setNameDialog({ kind: 'rename', template: t })}>
                      <Pencil aria-hidden="true" />
                      Rename
                    </DropdownMenuItem>
                  )}
                  {t.deletable && (
                    <DropdownMenuItem onSelect={() => setDeleting(t)}>
                      <Trash2 aria-hidden="true" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          ))}
        </ul>
      )}
      <TemplateNameDialog state={nameDialog} onClose={() => setNameDialog(null)} />
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete template?"
        body={<p>Projects created from {deleting?.name ?? 'it'} keep their settings and still show its name.</p>}
        confirmLabel="Delete"
        destructive
        pending={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id, { onSuccess: () => (toast.success('Template deleted.'), setDeleting(null)) })}
      />
    </Page>
  );
}
